import { AttachmentHandleMode } from '../properties/types';
import { LoanType } from '@apply/constants';

const prefix = 'forms/';

export const RESET = `${prefix}RESET`;
export const UPDATE_VALUE = `${prefix}UPDATE_VALUE`;
export const UPDATE_NAME = `${prefix}UPDATE_NAME`;
export const ADD_EMPTY_FORM = `${prefix}ADD_EMPTY_FORM`;
export const CLONE_FORM = `${prefix}CLONE_FORM`;
export const DELETE_FORM = `${prefix}DELETE_FORM`;
export const REPLACE_FORM_STATE = `${prefix}REPLACE_FORM_STATE`;

export const UPDATE_COLLECTION_IS_DIRTY = `${prefix}UPDATE_COLLECTION_IS_DIRTY`;
export const UPDATE_FORM_IS_DIRTY = `${prefix}UPDATE_FORM_IS_DIRTY`;
export const REPLACE_COLLECTION_STATE = `${prefix}REPLACE_COLLECTION_STATE`;

export type BudgetCategory =
    | 'Land Cost'
    | 'Site Work'
    | 'Foundations'
    | 'Foundations (Largest Subcontractor or Supplier)'
    | 'Framing'
    | 'Framing (Largest Subcontractor or Supplier)'
    | 'Exterior Finishes'
    | 'Major Systems Rough-ins'
    | 'Electrical (Largest Subcontractor or Supplier)'
    | 'HVAC (Largest Subcontractor or Supplier)'
    | 'Plumbing (Largest Subcontractor or Supplier)'
    | 'Interior Finishes'
    | 'Final Steps';

export type ValueSpec = {
    name: string;
    advisory?: string;
    datatype: 'string' | 'amount' | 'tel' | 'otherdescribe' | 'amountwithdescription' | 'longstring';
    optional?: boolean;
};
export type CategorySpec = {
    category: BudgetCategory | LandBudgetCategory | FixFlipBudgetCategory | SpecSheetCategory;
    values: ValueSpec[];
};
export type BudgetSpec = CategorySpec[];

export const landCostSpec: CategorySpec = {
    category: 'Land Cost',
    values: [
        {
            name: 'Land Cost',
            advisory: 'must tie to purchase & sale contract',
            datatype: 'amount',
            optional: true,
        },
        { name: 'Other, Describe', datatype: 'otherdescribe', optional: true },
    ],
};

export const siteWorkSpec: CategorySpec = {
    category: 'Site Work',
    values: [
        { name: 'Demolition', datatype: 'amount', optional: true },
        { name: 'Building Permit Fees', datatype: 'amount', optional: true },
        { name: 'Utilities', datatype: 'amount', optional: true },
        { name: 'Inspections', datatype: 'amount', optional: true },
        {
            name: 'Architecture, Engineering',
            datatype: 'amount',
            optional: true,
        },
        { name: 'Clearing & Grading', datatype: 'amount', optional: true },
        { name: 'Well/Water', datatype: 'amount', optional: true },
        { name: 'Septic/Sewer', datatype: 'amount', optional: true },
        { name: 'Other, Describe', datatype: 'otherdescribe', optional: true },
    ],
};

export const foundationsSpec: CategorySpec = {
    category: 'Foundations',
    values: [
        {
            name: 'Excavations and Backfill',
            datatype: 'amount',
            optional: true,
        },
        { name: 'Retaining Walls', datatype: 'amount', optional: true },
        {
            name: 'Footings, Foundation and Concrete',
            datatype: 'amount',
            optional: true,
        },
        { name: 'Slabs', datatype: 'amount', optional: true },
        { name: 'Other, Describe', datatype: 'otherdescribe', optional: true },
    ],
};

export const foundationsContractorSpec: CategorySpec = {
    category: 'Foundations (Largest Subcontractor or Supplier)',
    values: [
        { name: 'Name', datatype: 'string', optional: true },
        { name: 'Phone', datatype: 'tel', optional: true },
    ],
};

export const framingSpec: CategorySpec = {
    category: 'Framing',
    values: [
        {
            name: 'Framing',
            advisory: 'including roof',
            datatype: 'amount',
            optional: true,
        },
        {
            name: 'Trusses',
            advisory: 'if not included above',
            datatype: 'amount',
            optional: true,
        },
        {
            name: 'Sheathing',
            advisory: 'if not included above',
            datatype: 'amount',
            optional: true,
        },
        { name: 'General Metal, Steel', datatype: 'amount', optional: true },
        { name: 'Other, Describe', datatype: 'otherdescribe', optional: true },
    ],
};

export const framingContractorSpec: CategorySpec = {
    category: 'Framing (Largest Subcontractor or Supplier)',
    values: [
        { name: 'Name', datatype: 'string', optional: true },
        { name: 'Phone', datatype: 'tel', optional: true },
    ],
};

export const exteriorFinishesSpec: CategorySpec = {
    category: 'Exterior Finishes',
    values: [
        { name: 'Exterior Wall Finish', datatype: 'amount', optional: true },
        { name: 'Roofing', datatype: 'amount', optional: true },
        { name: 'Windows & Doors', datatype: 'amount', optional: true },
        { name: 'Garage Door', datatype: 'amount', optional: true },
        { name: 'Other, Describe', datatype: 'otherdescribe', optional: true },
    ],
};

export const majorSystemsSpec: CategorySpec = {
    category: 'Major Systems Rough-ins',
    values: [
        {
            name: 'Plumbing',
            advisory: 'except fixtures',
            datatype: 'amount',
            optional: true,
        },
        {
            name: 'Electrical',
            advisory: 'except fixtures',
            datatype: 'amount',
            optional: true,
        },
        { name: 'HVAC', datatype: 'amount', optional: true },
        { name: 'Other, Describe', datatype: 'otherdescribe', optional: true },
    ],
};

export const electricalContractorSpec: CategorySpec = {
    category: 'Electrical (Largest Subcontractor or Supplier)',
    values: [
        { name: 'Name', datatype: 'string', optional: true },
        { name: 'Phone', datatype: 'tel', optional: true },
    ],
};

export const hvacContractorSpec: CategorySpec = {
    category: 'HVAC (Largest Subcontractor or Supplier)',
    values: [
        { name: 'Name', datatype: 'string', optional: true },
        { name: 'Phone', datatype: 'tel', optional: true },
    ],
};

export const plumbingContractorSpec: CategorySpec = {
    category: 'Plumbing (Largest Subcontractor or Supplier)',
    values: [
        { name: 'Name', datatype: 'string', optional: true },
        { name: 'Phone', datatype: 'tel', optional: true },
    ],
};

export const interiorFinishesSpec: CategorySpec = {
    category: 'Interior Finishes',
    values: [
        { name: 'Insulation', datatype: 'amount', optional: true },
        { name: 'Drywall', datatype: 'amount', optional: true },
        {
            name: 'Interior Trims, Doors and Mirrors',
            datatype: 'amount',
            optional: true,
        },
        { name: 'Painting', datatype: 'amount', optional: true },
        { name: 'Lighting', datatype: 'amount', optional: true },
        { name: 'Cabinets, Countertops', datatype: 'amount', optional: true },
        { name: 'Appliances', datatype: 'amount', optional: true },
        { name: 'Flooring', datatype: 'amount', optional: true },
        { name: 'Plumbing Fixtures', datatype: 'amount', optional: true },
        { name: 'Fireplaces', datatype: 'amount', optional: true },
        { name: 'Other, Describe', datatype: 'otherdescribe', optional: true },
    ],
};

export const finalStepsSpec: CategorySpec = {
    category: 'Final Steps',
    values: [
        { name: 'Landscaping', datatype: 'amount', optional: true },
        {
            name: 'Outdoor Structures',
            advisory: 'deck, patio, porches',
            datatype: 'amount',
            optional: true,
        },
        { name: 'Driveway & Walks', datatype: 'amount', optional: true },
        { name: 'Clean Up', datatype: 'amount', optional: true },
        { name: 'Other, Describe', datatype: 'otherdescribe', optional: true },
    ],
};

export const budgetSpec: BudgetSpec = [
    landCostSpec,
    siteWorkSpec,
    foundationsSpec,
    foundationsContractorSpec,
    framingSpec,
    framingContractorSpec,
    exteriorFinishesSpec,
    majorSystemsSpec,
    electricalContractorSpec,
    hvacContractorSpec,
    plumbingContractorSpec,
    interiorFinishesSpec,
    finalStepsSpec,
];

export const generalContractorBudgetSpec: BudgetSpec = [
    landCostSpec,
    siteWorkSpec,
    foundationsSpec,
    framingSpec,
    exteriorFinishesSpec,
    majorSystemsSpec,
    interiorFinishesSpec,
    finalStepsSpec,
];

export type LandBudgetCategory = 'Land Cost' | 'Setup' | 'Utilities' | 'Site Work';

export const landSetupSpec: CategorySpec = {
    category: 'Setup',
    values: [
        { name: 'Governmental Fees', datatype: 'amount', optional: true },
        { name: 'Engineering', datatype: 'amount', optional: true },
        { name: 'Other, Describe', datatype: 'otherdescribe', optional: true },
    ],
};

export const landUtilities: CategorySpec = {
    category: 'Utilities',
    values: [
        { name: 'Electric Lines', datatype: 'amount', optional: true },
        { name: 'Gas Lines', datatype: 'amount', optional: true },
        { name: 'Sprinklers', datatype: 'amount', optional: true },
        {
            name: 'Telephone, Cable, and Fiber Optic Lines',
            datatype: 'amount',
            optional: true,
        },
        { name: 'Other, Describe', datatype: 'otherdescribe', optional: true },
    ],
};

export const landSiteWork: CategorySpec = {
    category: 'Site Work',
    values: [
        { name: 'Demolition', datatype: 'amount', optional: true },
        { name: 'Clearing', datatype: 'amount', optional: true },
        { name: 'Grading', datatype: 'amount', optional: true },
        { name: 'Paving', datatype: 'amount', optional: true },
        { name: 'Sidewalks', datatype: 'amount', optional: true },
        { name: 'Curbs', datatype: 'amount', optional: true },
        { name: 'Gutters', datatype: 'amount', optional: true },
        { name: 'Sewers', datatype: 'amount', optional: true },
        { name: 'Landscaping', datatype: 'amount', optional: true },
        { name: 'Hardscaping', datatype: 'amount', optional: true },
        { name: 'Other, Describe', datatype: 'otherdescribe', optional: true },
    ],
};

export const landBudgetSpec: BudgetSpec = [landCostSpec, landSetupSpec, landUtilities, landSiteWork];

export type FixFlipBudgetCategory =
    | 'Property Cost'
    | 'Exterior Structure'
    | 'Major Systems'
    | 'Interior'
    | 'Expand Primary Structure'
    | 'Final Structure';

export const fixFlipPropertyCostSpec: CategorySpec = {
    category: 'Property Cost',
    values: [
        {
            name: 'Acquisition Price',
            advisory: 'must tie to purchase contract',
            datatype: 'amount',
            optional: true,
        },
        { name: 'Initial Inspection', datatype: 'amount', optional: true },
        {
            name: 'Other Work',
            datatype: 'amountwithdescription',
            optional: true,
        },
    ],
};

export const fixFlipSiteWorkSpec: CategorySpec = {
    category: 'Site Work',
    values: [
        {
            name: 'Demolition',
            datatype: 'amountwithdescription',
            optional: true,
        },
        {
            name: 'Building Permit Fees',
            datatype: 'amountwithdescription',
            optional: true,
        },
        {
            name: 'Architecture, Engineering',
            datatype: 'amountwithdescription',
            optional: true,
        },
        {
            name: 'Other Work',
            datatype: 'amountwithdescription',
            optional: true,
        },
    ],
};

export const fixFlipExteriorStructureSpec: CategorySpec = {
    category: 'Exterior Structure',
    values: [
        {
            name: 'Painting, Siding, Wall Finish',
            datatype: 'amountwithdescription',
            optional: true,
        },
        {
            name: 'Guters or Fascia, Downspout',
            datatype: 'amountwithdescription',
            optional: true,
        },
        {
            name: 'Porch or Deck',
            datatype: 'amountwithdescription',
            optional: true,
        },
        { name: 'Roofing', datatype: 'amountwithdescription', optional: true },
        {
            name: 'Windows and Doors',
            datatype: 'amountwithdescription',
            optional: true,
        },
        {
            name: 'Garage Door',
            datatype: 'amountwithdescription',
            optional: true,
        },
        {
            name: 'Other Work',
            datatype: 'amountwithdescription',
            optional: true,
        },
    ],
};

export const fixFlipMajorSystemsSpec: CategorySpec = {
    category: 'Major Systems',
    values: [
        {
            name: 'Plumbing',
            advisory: 'except fixtures',
            datatype: 'amountwithdescription',
            optional: true,
        },
        {
            name: 'Electrical',
            advisory: 'except fixtures',
            datatype: 'amountwithdescription',
            optional: true,
        },
        { name: 'HVAC', datatype: 'amountwithdescription', optional: true },
        {
            name: 'Sewer or Septic',
            datatype: 'amountwithdescription',
            optional: true,
        },
        {
            name: 'Other Work',
            datatype: 'amountwithdescription',
            optional: true,
        },
    ],
};

export const fixFlipInteriorSpec: CategorySpec = {
    category: 'Interior',
    values: [
        {
            name: 'Insulation',
            datatype: 'amountwithdescription',
            optional: true,
        },
        { name: 'Drywall', datatype: 'amountwithdescription', optional: true },
        {
            name: 'Interior Trims, Doors, and Mirrors',
            datatype: 'amountwithdescription',
            optional: true,
        },
        { name: 'Lighting', datatype: 'amountwithdescription', optional: true },
        {
            name: 'Cabinets and Countertops',
            datatype: 'amountwithdescription',
            optional: true,
        },
        {
            name: 'Appliances',
            datatype: 'amountwithdescription',
            optional: true,
        },
        { name: 'Flooring', datatype: 'amountwithdescription', optional: true },
        {
            name: 'Plumbing Fixtures',
            datatype: 'amountwithdescription',
            optional: true,
        },
        {
            name: 'Fireplace',
            datatype: 'amountwithdescription',
            optional: true,
        },
        {
            name: 'Other Work',
            datatype: 'amountwithdescription',
            optional: true,
        },
    ],
};

export const fixFlipExpandPrimaryStructureSpec: CategorySpec = {
    category: 'Expand Primary Structure',
    values: [
        { name: 'Item #1', datatype: 'amountwithdescription', optional: true },
        { name: 'Item #2', datatype: 'amountwithdescription', optional: true },
        { name: 'Item #3', datatype: 'amountwithdescription', optional: true },
        { name: 'Item #4', datatype: 'amountwithdescription', optional: true },
        { name: 'Item #5', datatype: 'amountwithdescription', optional: true },
    ],
};

export const fixFlipFinalStepsSpec: CategorySpec = {
    category: 'Final Structure',
    values: [
        {
            name: 'Landscaping',
            datatype: 'amountwithdescription',
            optional: true,
        },
        {
            name: 'Outdoor Structures',
            advisory: 'deck, patio, porches',
            datatype: 'amountwithdescription',
            optional: true,
        },
        {
            name: 'Driveway and Walks',
            datatype: 'amountwithdescription',
            optional: true,
        },
        { name: 'Clean Up', datatype: 'amountwithdescription', optional: true },
        {
            name: 'Other Work',
            datatype: 'amountwithdescription',
            optional: true,
        },
    ],
};

export const fixFlipBudgetSpec: BudgetSpec = [
    fixFlipPropertyCostSpec,
    fixFlipSiteWorkSpec,
    fixFlipExteriorStructureSpec,
    fixFlipMajorSystemsSpec,
    fixFlipInteriorSpec,
    fixFlipExpandPrimaryStructureSpec,
    fixFlipFinalStepsSpec,
];

export type SpecSheetCategory = 'Exterior Features' | 'Interior Features' | 'Additional Features';

export type SpecSheetSpec = CategorySpec[];

export const exteriorFeaturesSpec: CategorySpec = {
    category: 'Exterior Features',
    values: [
        { name: 'Roof', datatype: 'longstring', optional: true },
        { name: 'Siding', datatype: 'longstring', optional: true },
        { name: 'Trim', datatype: 'longstring', optional: true },
        { name: 'Gutters/Downspouts', datatype: 'longstring', optional: true },
        { name: 'Porch, Deck, Patio', datatype: 'longstring', optional: true },
        {
            name: 'Garage/Overhead Door',
            datatype: 'longstring',
            optional: true,
        },
        { name: 'Doors', datatype: 'longstring', optional: true },
        { name: 'Windows', datatype: 'longstring', optional: true },
        { name: 'Columns', datatype: 'longstring', optional: true },
        { name: 'Shutters', datatype: 'longstring', optional: true },
        { name: 'Driveway/Sidewalk', datatype: 'longstring', optional: true },
        { name: 'Landscaping', datatype: 'longstring', optional: true },
        { name: 'Other', datatype: 'longstring', optional: true },
    ],
};

export const interiorFeaturesSpec: CategorySpec = {
    category: 'Interior Features',
    values: [
        { name: 'Flooring', datatype: 'longstring', optional: true },
        { name: 'Walls', datatype: 'longstring', optional: true },
        { name: 'Ceilings', datatype: 'longstring', optional: true },
        { name: 'Trim', datatype: 'longstring', optional: true },
        { name: 'Doors', datatype: 'longstring', optional: true },
        { name: 'Shelving', datatype: 'longstring', optional: true },
        { name: 'Cabinets', datatype: 'longstring', optional: true },
        { name: 'Lighting', datatype: 'longstring', optional: true },
        { name: 'Appliances', datatype: 'longstring', optional: true },
        { name: 'Others', datatype: 'longstring', optional: true },
    ],
};

export const additionalFeaturesSpec: CategorySpec = {
    category: 'Additional Features',
    values: [
        { name: 'Kitchen', datatype: 'longstring', optional: true },
        { name: 'Bath', datatype: 'longstring', optional: true },
        { name: 'Energy Savers', datatype: 'longstring', optional: true },
        { name: 'Quality Assurance', datatype: 'longstring', optional: true },
        { name: 'Other', datatype: 'longstring', optional: true },
    ],
};

export const specSheetSpec: SpecSheetSpec = [exteriorFeaturesSpec, interiorFeaturesSpec, additionalFeaturesSpec];

export type FormRecordValueType = string | [string, string];
export type FormType = 'Budget' | 'Land Budget' | 'Fix-Flip' | 'Fix-Flip-LOC' | 'Spec Sheet';

export type FormState = {
    id: number | null | undefined;
    guid: string;
    propertyGuid: string;
    sharedFormId: number;
    type: FormType;
    loanType: LoanType;
    name: string;
    isDirty: boolean;
    values: Record<string, Record<string, FormRecordValueType>>;
};

export const formInitialState: FormState = {
    id: null,
    guid: '',
    propertyGuid: '',
    sharedFormId: 0,
    type: undefined,
    loanType: undefined,
    name: '',
    isDirty: false,
    values: {},
};

export type FormCollectionState = {
    forms: FormState[];
    isDirty: boolean;
    apiId: string;
};

export const formCollectionInitialState: FormCollectionState = {
    forms: [],
    isDirty: false,
    apiId: '',
};

export const areValuesEqual = (left: FormRecordValueType, right: FormRecordValueType) => {
    return JSON.stringify(left) === JSON.stringify(right);
};

export const defaultValue = (
    type: 'string' | 'amount' | 'tel' | 'otherdescribe' | 'amountwithdescription' | 'longstring'
): FormRecordValueType => (['otherdescribe', 'amountwithdescription'].includes(type) ? ['', ''] : '');

export const createEmptyFormValuesData = (type: FormType) => {
    const values: Record<string, Record<string, FormRecordValueType>> = {};
    if (type === 'Budget') {
        budgetSpec.forEach((catspec) => {
            values[catspec.category] = {};
            catspec.values.forEach((valspec) => {
                values[catspec.category][valspec.name] = defaultValue(valspec.datatype);
            });
        });
        return values;
    } else if (type === 'Land Budget') {
        landBudgetSpec.forEach((landspec) => {
            values[landspec.category] = {};
            landspec.values.forEach((valspec) => {
                values[landspec.category][valspec.name] = defaultValue(valspec.datatype);
            });
        });
        return values;
    } else if (type === 'Fix-Flip' || type === 'Fix-Flip-LOC') {
        fixFlipBudgetSpec.forEach((fixflipspec) => {
            values[fixflipspec.category] = {};
            fixflipspec.values.forEach((valspec) => {
                values[fixflipspec.category][valspec.name] = defaultValue(valspec.datatype);
            });
        });
        return values;
    } else if (type === 'Spec Sheet') {
        specSheetSpec.forEach((ssspec) => {
            values[ssspec.category] = {};
            ssspec.values.forEach((valspec) => {
                values[ssspec.category][valspec.name] = defaultValue(valspec.datatype);
            });
        });
        return values;
    } else {
        throw new Error('unhandled form type encoutered');
    }
};

export const getTotalAmountsFromForm = (form: FormState): number => {
    // currently only the budget form has totalable figures, if this
    // change in the future additional handling will need to be defined
    if (!form || !form.values || form.type === 'Spec Sheet') {
        return 0;
    }

    let spec;
    switch (form.type) {
        case 'Budget':
            spec = budgetSpec;
            break;
        case 'Fix-Flip':
        case 'Fix-Flip-LOC':
            spec = fixFlipBudgetSpec;
            break;
        case 'Land Budget':
            spec = landBudgetSpec;
            break;
    }

    let total = 0;
    spec?.forEach((catspec) => {
        catspec.values.forEach((valspec) => {
            if (valspec.datatype === 'amount' && form.values[catspec.category][valspec.name]) {
                total += Number(form.values[catspec.category][valspec.name]);
            } else if (['otherdescribe', 'amountwithdescription'].includes(valspec.datatype)) {
                total += Number(form.values[catspec.category][valspec.name][1]);
            }
        });
    });
    return total;
};

const isValidPhone = (phone: string): boolean => {
    const value = phone.replace(/[^0-9.]/g, '');
    return value.length === 10;
};

export const countOfNonCompliantValues = (form: FormState): number => {
    if (!form || !form.values) {
        return 0;
    }

    let spec;
    switch (form.type) {
        case 'Budget':
            spec = budgetSpec;
            break;
        case 'Spec Sheet':
            spec = specSheetSpec;
            break;
        case 'Fix-Flip':
        case 'Fix-Flip-LOC':
            spec = fixFlipBudgetSpec;
            break;
        case 'Land Budget':
            spec = landBudgetSpec;
            break;
    }

    let count = 0;
    spec?.forEach((catspec) => {
        catspec.values.forEach((valspec) => {
            const value = form.values[catspec.category][valspec.name];
            if (Array.isArray(value)) {
                // istanbul ignore if
                if (!valspec.optional) {
                    if (value.some((val) => !val)) {
                        count++;
                    }
                }
            } else {
                // istanbul ignore next
                if (!valspec.optional && !value) {
                    count++;
                } else if (valspec.datatype === 'tel' && !!value && !isValidPhone(value)) {
                    count++;
                }
            }
        });
    });
    return count;
};

export const isFormDataStructureEmpty = (data: Record<string, Record<string, FormRecordValueType>>) => {
    for (const prop in data) {
        for (const innerProp in data[prop]) {
            const value = data[prop][innerProp];
            if (Array.isArray(value)) {
                for (const sub of value) {
                    if (sub?.length > 0) {
                        return false;
                    }
                }
            } else if (typeof value == 'number' && value !== 0) {
                return false;
            } else if (value?.length > 0) {
                return false;
            }
        }
    }
    return true;
};

export const formConstant = {
    displayName: {
        budget: 'Budget',
        landBudget: 'Land Budget',
        fixFlipBudget: 'Fix-Flip',
        specSheet: 'Spec Sheet',
    },
    displayLowerCase: {
        budget: 'budget',
        landBudget: 'land budget',
        fixFlipBudget: 'fix-flip',
        specSheet: 'spec sheet',
    },
    name: {
        budget: 'budget',
        landBudget: 'landbudget',
        fixFlipBudget: 'fixFlipBudget',
        specSheet: 'specsheet',
        review: 'review',
    },
};

export const formAction = {
    create: 'createnewform' as AttachmentHandleMode,
    edit: 'editsavedform' as AttachmentHandleMode,
    upload: 'uploadfile' as AttachmentHandleMode,
    provideLater: 'providelater' as AttachmentHandleMode,
    willNotProvide: 'willnotprovide' as AttachmentHandleMode,
    unset: 'unset' as AttachmentHandleMode,
    textData: 'textdata' as AttachmentHandleMode,
};
